/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */
/* eslint-disable no-await-in-loop */

import objectHash from 'object-hash';

import { arrayPartition, arraysToSet } from '../../../util.type';
import { fetchWithTimeout } from '../../fetch/fetch';
import { getUri } from '../../medium/medium';
import PromiseUtil from '../../promise/promise-util';
import { expand } from '../../uri-template/uri-template';
import { ErrorType } from './error-type';
import VastAnalyticsTracker from './vast-analytics-tracker';
import { parseVAST } from './vast-parser';
import {
  getWrapperNextUrl,
  replaceAllMacro,
  replaceErrorCodeMacro,
  sortBySequence
} from './vast-util';

/**
 * @import { AdInline, AdInlineResult, AdWrapper, AdWrapperResult, Creative, MediaFile, PlaylistWithoutId, VAST, VastPlaylist, VastTrack, Wrapper } from '.'
 * @import { IAudienceMultiplier } from '../../audience-multiplicator'
 * @import { ContentInfo } from '../../content/info/content-info'
 * @import { Medium } from "../../medium/medium"
 * @import { IVariablesNamespaceRegistry } from '../../variables'
 * @import { IExternalSourceFactory } from '..'
 */

/**
 * @implements {IExternalSourceFactory}
 */
export default class ExternalSourceVastFactory {
  /**
   * @param {IAudienceMultiplier} audienceMultiplier
   * @param {IVariablesNamespaceRegistry} variablesNamespaceRegistry
   * @param {Object} [timeouts]
   * @param {number} [timeouts.totalTimeoutBudgetMs]
   * @param {number} [timeouts.perRequestTimeoutMs]
   */
  constructor(
    audienceMultiplier,
    variablesNamespaceRegistry,
    { totalTimeoutBudgetMs = 15000, perRequestTimeoutMs = 8000 } = {}
  ) {
    this.audienceMultiplier = audienceMultiplier;
    this.variablesNamespaceRegistry = variablesNamespaceRegistry;
    this.perRequestTimeoutMs = perRequestTimeoutMs;
    this.totalTimeoutBudgetMs = totalTimeoutBudgetMs;
  }

  /** @type {IExternalSourceFactory['create']} */
  async create(contentInfo, medium) {
    const headers = new Headers({
      Accept: 'application/xml, text/xml;q=0.9, */*;q=0.8'
    });
    const url = expand(getUri(medium), this.variablesNamespaceRegistry);
    const adInlineResults = await resolveVastUrl(url, {
      fetchFn: (input, timeoutMs) =>
        fetchWithTimeout(input, { headers }, timeoutMs).then((res) =>
          res.text()
        ),
      perRequestTimeoutMs: this.perRequestTimeoutMs,
      totalTimeoutBudgetMs: this.totalTimeoutBudgetMs
    });

    const [successes, errors] = arrayPartition(
      adInlineResults,
      (r) => r.error === undefined
    );

    // Umyslně se nečeká na dokončení.
    errors.forEach((adInlineResult) => {
      adInlineResult.errorUrls.forEach((errorUrl) => {
        if (adInlineResult.error === undefined) {
          return;
        }
        let newUrl = replaceErrorCodeMacro(
          errorUrl,
          adInlineResult.error?.type
        );
        newUrl = replaceAllMacro(newUrl);
        // TODO: jak správně ošetřit chybu?
        fetchWithTimeout(newUrl, { method: 'GET' }).catch(() => {});
      });
    });
    const tracks = successes
      .map((adInlineResult) =>
        tracksFromAdInlineResult(adInlineResult, contentInfo, medium)
      )
      .flat()
      .filter((t) => t !== undefined);

    const playlist = playlistFromMediumExternal(medium, contentInfo, tracks);
    const analyticsTracker = new VastAnalyticsTracker(
      this.audienceMultiplier,
      tracks
    );
    return {
      destroy: () => playlist.destroy(),
      playlist,
      analyticsTracker
    };
  }
}

/**
 * @param {string} startUrl
 * @param {Object} fetchOpts
 * @param {number} fetchOpts.perRequestTimeoutMs
 * @param {number} fetchOpts.totalTimeoutBudgetMs
 * @param {(url: string, timeoutMs: number) => Promise<string>} fetchOpts.fetchFn
 * @returns {Promise<AdInlineResult[]>}
 */
export async function resolveVastUrl(
  startUrl,
  { perRequestTimeoutMs, totalTimeoutBudgetMs, fetchFn }
) {
  const { durationMs, result } = await PromiseUtil.measureTime(() =>
    fetchFn(startUrl, perRequestTimeoutMs)
  );
  const vast = parseVAST(result);
  if (!vast.Ad || vast.Ad.length === 0) {
    throw new Error('No Ads in root VAST');
  }

  vast.Ad.sort(sortBySequence);
  /** @type {AdInlineResult[]} */
  return adInlineResultFromAd(vast.Ad, {
    // Na první úrovni je povoleno více Ads.
    errorUrls: vast.Error ?? [],
    fetchFn,
    impressionUrls: [],
    depth: 0,
    paths: new Set([startUrl]),
    perRequestTimeoutMs,
    remainingTimeBudgetMs: totalTimeoutBudgetMs - durationMs
  });
}

/**
 * @param {(AdWrapper | AdInline)[]} ads
 * @param {Object} deps
 * @param {string[]} deps.errorUrls
 * @param {string[]} deps.impressionUrls
 * @param {number} deps.depth
 * @param {Set<string>} deps.paths
 * @param {number} deps.perRequestTimeoutMs
 * @param {number} deps.remainingTimeBudgetMs
 * @param {(url: string, timeoutMs: number) => Promise<string>} deps.fetchFn
 * @returns {Promise<AdInlineResult[]>}
 */
async function adInlineResultFromAd(
  ads,
  {
    errorUrls,
    impressionUrls,
    depth,
    paths,
    fetchFn,
    perRequestTimeoutMs,
    remainingTimeBudgetMs
  }
) {
  /** @type {AdInlineResult[]} */
  const result = [];
  for (const ad of ads) {
    if (ad.type === 'inline' && ad.InLine && ad.InLine.Creatives) {
      const inLine = ad.InLine;
      result.push({
        depth,
        errorUrls: arraysToSet(inLine?.Error ?? [], errorUrls),
        impressionUrls: arraysToSet(inLine?.Impression ?? [], impressionUrls),
        inlines: inLine,
        paths
      });
    } else if (ad.type === 'wrapper' && ad.Wrapper) {
      const wrapper = ad.Wrapper;
      result.push(
        ...(await adInlineResultFromWrapper(
          {
            allowMultipleAds: wrapper.AllowMultipleAds,
            depth: depth + 1,
            errorUrls: [...(wrapper.Error ?? []), ...errorUrls],
            impressionUrls: [...(wrapper.Impression ?? []), ...impressionUrls],
            paths,
            perRequestTimeoutMs,
            remainingTimeBudgetMs,
            wrapper
          },
          fetchFn
        ))
      );
    } else {
      result.push({
        depth,
        error: {
          message: `Ad with id ${ad.id} is missing required ${
            ad.type === 'inline' ? 'InLine' : 'Wrapper'
          } element`,
          type: ErrorType.NoAds
        },
        errorUrls: arraysToSet(errorUrls),
        impressionUrls: arraysToSet(impressionUrls),
        paths
      });
    }
  }
  return result;
}

/**
 * @param {AdWrapperResult} adWrapperResult
 * @param {(url: string, timeoutMs: number) => Promise<string>} fetchFn
 * @returns {Promise<AdInlineResult[]>}
 */
async function adInlineResultFromWrapper(adWrapperResult, fetchFn) {
  const { depth, paths, perRequestTimeoutMs, remainingTimeBudgetMs, wrapper } =
    adWrapperResult;
  const nextUrl = getWrapperNextUrl(wrapper);
  if (paths.has(nextUrl) || depth > 5) {
    return adInlineResultError(
      adWrapperResult,
      ErrorType.WrapperDepthLoop,
      `Loop detected or max depth exceeded in wrapper chain: ${nextUrl}`
    );
  }

  /** @type {string | undefined} */
  let nextXml;
  let durationMs = 0;
  try {
    if (remainingTimeBudgetMs <= 0) {
      return adInlineResultError(
        adWrapperResult,
        ErrorType.ChainTimeout,
        `Total chain time budget has been exceeded ${nextUrl}.`
      );
    }
    const { durationMs: fetchDurationMs, result } =
      await PromiseUtil.measureTime(() =>
        fetchFn(nextUrl, Math.min(remainingTimeBudgetMs, perRequestTimeoutMs))
      );
    nextXml = result;
    durationMs = fetchDurationMs;
    paths.add(nextUrl);
  } catch (err) {
    return adInlineResultError(
      adWrapperResult,
      ErrorType.RuntimeError,
      `Failed to fetch VAST at ${nextUrl}: ${
        /** @type {Error} */ (err).message
      }`
    );
  }
  /** @type {VAST | undefined} */
  let nextVast;
  try {
    nextVast = parseVAST(nextXml);
    nextVast.Ad?.sort(sortBySequence);
    paths.add(nextUrl);
  } catch (err) {
    return adInlineResultError(
      adWrapperResult,
      ErrorType.XmlParsingError,
      `Failed to parse VAST at ${nextUrl}: ${
        /** @type {Error} */ (err).message
      }`
    );
  }

  let ads = nextVast.Ad ?? [];
  if (!adWrapperResult.allowMultipleAds) {
    // Pokud není allowMultipleAds !== true, tak se bere první stand-alone
    // (Ad bez vyplněného sequence).
    const ad = ads.find((a) => a.sequence === undefined);
    ads = ad ? [ad] : [];
  }

  if (ads.length === 0) {
    return adInlineResultError(
      adWrapperResult,
      ErrorType.NoAds,
      `No Ads in VAST at ${nextUrl}`
    );
  }

  /** @type {AdInlineResult[]} */
  return adInlineResultFromAd(ads, {
    ...adWrapperResult,
    depth,
    paths,
    remainingTimeBudgetMs: remainingTimeBudgetMs - durationMs,
    fetchFn
  });
}

/**
 * @param {AdWrapperResult} adWrapperResult
 * @param {typeof ErrorType[keyof typeof ErrorType]} type
 * @param {string} message
 * @returns {AdInlineResult[]}
 */
function adInlineResultError(adWrapperResult, type, message) {
  const errorUrls = arraysToSet(adWrapperResult.errorUrls ?? []);
  const impressionUrls = arraysToSet(adWrapperResult.impressionUrls ?? []);
  return [
    {
      ...adWrapperResult,
      error: { type, message },
      errorUrls,
      impressionUrls,
      inlines: undefined
    }
  ];
}

/**
 * @param {Medium} medium
 * @param {ContentInfo} contentInfo
 * @param {VastTrack[]} tracks
 * @returns {VastPlaylist}
 */
export function playlistFromMediumExternal(medium, contentInfo, tracks) {
  /** @type {PlaylistWithoutId} */
  const playlistWithoutId = {
    name: medium.name,
    volume: medium.volume,
    cut: 0,
    deterministic: false,
    scope: '',
    scheduleEnum: 'STATIC',
    tracks: []
  };
  const playlist = contentInfo.addTransientPlaylist(playlistWithoutId);
  // TODO: track potřebuje id playlistu, ale to je známé až po volání addTransientPlaylist.
  // @ts-expect-error
  playlist.tracks = tracks.map((t) => ({
    ...t,
    id: objectHash({ parentPlaylistId: playlist.id, index: t.index }),
    parentPlaylistId: playlist.id
  }));
  return {
    ...playlist,
    destroy: () => {
      tracks.forEach((t) => t.destroy());
      contentInfo.deleteTransientPlaylist(playlist);
    }
  };
}

/**
 * @param {AdInlineResult} adInlineResult
 * @param {ContentInfo} contentInfo
 * @param {Medium} localMedium
 * @returns {(VastTrack | undefined)[]}
 */
export function tracksFromAdInlineResult(
  adInlineResult,
  contentInfo,
  localMedium
) {
  if (adInlineResult.error) {
    return [];
  }
  const creatives = adInlineResult.inlines?.Creatives ?? [];
  creatives.sort(sortBySequence);

  return creatives.map((creative, index) => {
    const mediaFiles = creative.Linear?.MediaFiles ?? [];
    if (mediaFiles.length === 0) {
      return undefined;
    }
    const mediumWithoutId = mediumFromCreative(creative, localMedium);
    if (!mediumWithoutId) {
      return undefined;
    }

    const medium = contentInfo.addTransientMedium(mediumWithoutId);
    /** @type {VastTrack} */
    const track = {
      destroy: () => contentInfo.deleteTransientMedium(medium),
      index,
      medium,
      mediumId: medium.id,
      playDuration: 0,
      volume: 1,
      trackingEvents: creative.Linear?.TrackingEvents,
      impressionUrls: adInlineResult.impressionUrls
    };
    return track;
  });
}

/**
 * @param {Creative} creative
 * @param {Medium} localMedium
 * @returns {Omit<Medium, 'id'> | undefined}
 */
function mediumFromCreative(creative, localMedium) {
  const mediaFile = bestMediaFile(creative.Linear?.MediaFiles ?? []);
  if (!mediaFile) {
    return undefined;
  }
  const url = mediaFile.uri;
  return {
    name: creative.id,
    onlineUrl: url,
    playDuration: 0,
    url,
    uri: url,
    volume: localMedium.volume,
    stretchEnum: localMedium.stretchEnum,
    type: mediaFile.type ?? ''
  };
}

/**
 * Vybere nejlepšího kandidáta podle definovaného algoritmu
 * @param {MediaFile[]} candidates
 * @returns {MediaFile | undefined}
 */
function bestMediaFile(candidates) {
  if (!Array.isArray(candidates) || candidates.length === 0) {
    return undefined;
  }

  const supportedMimeTypes = new Set(['video/mp4']);

  // 1) Normalizuj type + čísla + url
  let normalized = candidates.map((c) => {
    const type = c.type ?? '';
    return {
      original: c,
      url: c.uri,
      type,
      delivery: c.delivery || '',
      bitrate: Number(c.bitrate) || 0,
      width: Number(c.width) || 0,
      height: Number(c.height) || 0
    };
  });

  // 2) Filtruj podporované MIME typy
  const supported = normalized.filter((c) => supportedMimeTypes.has(c.type));

  if (supported.length > 0) {
    normalized = supported;
  }

  // 3) Preferuj progressive delivery
  const progressive = normalized.filter((c) => c.delivery === 'progressive');
  if (progressive.length > 0) {
    normalized = progressive;
  }

  // 4) Preferuj height ≤ 1080
  const le1080 = normalized.filter((c) => c.height <= 1080);
  if (le1080.length > 0) {
    normalized = le1080;
  }

  // 5) Sort
  normalized.sort((a, b) => {
    // a) height desc
    if (b.height !== a.height) {
      return b.height - a.height;
    }

    // b) width desc
    if (b.width !== a.width) {
      return b.width - a.width;
    }

    // c) bitrate desc
    if (b.bitrate !== a.bitrate) {
      return b.bitrate - a.bitrate;
    }

    // d) prefer MP4
    if (a.type === 'video/mp4' && b.type !== 'video/mp4') {
      return -1;
    }

    if (b.type === 'video/mp4' && a.type !== 'video/mp4') {
      return 1;
    }

    return 0;
  });

  // 6) Return first original object
  return normalized.length > 0 ? normalized[0].original : undefined;
}
